import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../store/authStore';
import Button from './Button';
import { X, MapPin, Globe } from 'lucide-react';
import supabase from "../lib/supabase";  // ✅ Use single instance directly

const schema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  start_time: z.string().min(1, 'Start time is required'),
  end_time: z.string().min(1, 'End time is required'),
  is_online: z.boolean(),
  location: z.string().optional(),
  meeting_url: z.string().url().optional().or(z.literal('')),
});

type FormData = z.infer<typeof schema>;

interface CreateEventModalProps {
  communityId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreateEventModal({ communityId, onClose, onSuccess }: CreateEventModalProps) {
  const { user } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      is_online: false,
    }
  });

  const isOnline = watch('is_online');

  const onSubmit = async (data: FormData) => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Validate that end time is after start time
      const startTime = new Date(data.start_time);
      const endTime = new Date(data.end_time);

      if (endTime <= startTime) {
        setError('End time must be after start time');
        return;
      }

      const { error: insertError } = await supabase
        .from('community_events')
        .insert({
          community_id: communityId,
          creator_id: user.id,
          title: data.title,
          description: data.description,
          start_time: data.start_time,
          end_time: data.end_time,
          is_online: data.is_online,
          location: !data.is_online ? data.location : null,
          meeting_url: data.is_online ? data.meeting_url : null,
        });

      if (insertError) throw insertError;

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to create event:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-10">
      <div className="bg-surface-light rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-secondary border-b border-secondary p-4 flex items-start justify-between shadow-md z-10 mb-6">
          <h2 className="text-3xl font-bold font-dillan text-neutral">Create New Event</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          {error && (
            <div className="bg-secondary-dark bg-opacity-10 text-secondary-dark p-4 rounded-xl">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <input
              type="text"
              {...register('title')}
              className="w-full px-3 py-2 bg-neutral border border-neutral rounded-full text-surface-light focus:outline-none focus:ring-0 focus:ring-secondary placeholder:text-surface-light font-dillant"
              placeholder="Enter event title"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-secondary-dark">{errors.title.message}</p>
            )}
          </div>

          <div>
            <textarea
              {...register('description')}
              className="w-full px-3 py-2 pl-4 pt-3 bg-neutral border border-neutral rounded-full text-surface-light focus:outline-none focus:ring-0 focus:ring-secondary placeholder:text-surface-light font-dillan"
              placeholder="Describe your event"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-secondary-dark">{errors.description.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-4 space-y-4 sm:space-y-0">
            <div>
              <label className="block text-sm font-medium text-primary mb-1 pl-2">
                Start Time
              </label>
              <input
                type="datetime-local"
                {...register('start_time')}
                className="w-full px-3 py-2 bg-neutral border border-neutral rounded-full text-surface-light focus:outline-none focus:ring-0 focus:ring-secondary placeholder:text-surface-light font-dillan"
              />
              {errors.start_time && (
                <p className="mt-1 text-sm text-secondary-dark">{errors.start_time.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-1 pl-2">
                End Time
              </label>
              <input
                type="datetime-local"
                {...register('end_time')}
                className="w-full px-3 py-2 bg-neutral border border-neutral rounded-full text-surface-light focus:outline-none focus:ring-0 focus:ring-secondary placeholder:text-surface-light font-dillan"
              />
              {errors.end_time && (
                <p className="mt-1 text-sm text-secondary-dark">{errors.end_time.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="flex items-center space-x-2 pl-2">
              <input
                type="checkbox"
                {...register('is_online')}
                className="w-4 h-4 border-2 border-primary rounded-full 
      appearance-none transition-all 
      checked:bg-primary checked:border-primary pl-2"
              />
              <span className="text-sm text-primary">This is an online event</span>
            </label>
          </div>

          {isOnline ? (
            <div>
              <div className="relative">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-light" />
                <input
                  type="url"
                  {...register('meeting_url')}
                  className="w-full px-3 py-2 pl-12 bg-neutral border border-neutral rounded-full text-surface-light focus:outline-none focus:ring-0 focus:ring-secondary placeholder:text-surface-light font-dillan"
                  placeholder="Meeting URL"
                />
              </div>
              {errors.meeting_url && (
                <p className="mt-1 text-sm text-secondary-dark">{errors.meeting_url.message}</p>
              )}
            </div>
          ) : (
            <div>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-light" />
                <input
                  type="text"
                  {...register('location')}
                  className="w-full px-3 py-2 pl-12 bg-neutral border border-neutral rounded-full text-surface-light focus:outline-none focus:ring-0 focus:ring-secondary placeholder:text-surface-light font-dillan"
                  placeholder="Enter event location"
                />
              </div>
              {errors.location && (
                <p className="mt-1 text-sm text-secondary-dark">{errors.location.message}</p>
              )}
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 bg-secondary text-neutral hover:bg-primary-light hover:text-primary border border-secondary hover:border-primary-light"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1 bg-secondary text-neutral hover:bg-primary-light hover:text-primary border border-secondary hover:border-primary-light"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}